import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import {
  Plus, Search, Filter, Loader2, X, Trash2, ImagePlus, Copy,
  ChevronDown, Eye, Save, Send as Publish, Pencil, MoreHorizontal,
  Tag, FolderPlus, ArrowUpDown, Upload, Download, AlertTriangle, ExternalLink,
} from "lucide-react";
import api, { inr, resolveImg, formatApiErrorDetail } from "../lib/api";

const STATUS_BADGE = {
  published: "bg-green-100 text-green-700",
  draft: "bg-amber-100 text-amber-700",
  unpublished: "bg-secondary text-muted-foreground",
  archived: "bg-red-100 text-red-700",
};

const EMPTY_PRODUCT = {
  name: "", sku: "", category: "", category_slug: "", subcategory: "", description: "", short_description: "",
  material: "", colour: "", dimensions: "", weight: "", shape: "", finish: "", care: "",
  country_of_origin: "India", product_type: "", set_contents: "", num_pieces: null,
  price: "", mrp: "", cost_price: "", tax_rate: "",
  stock_quantity: "", low_stock_threshold: 5, in_stock: true, track_inventory: false,
  images: [], image_alt_texts: {}, tags: [],
  status: "draft", featured: false, new_arrival: false, best_seller: false,
  seo_title: "", meta_description: "", url_slug: "", variants: [], custom_specs: {},
};

/* ============ Category Manager Modal ============ */
function CategoryManager({ open, onClose, categories, onRefresh }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api.post("/admin/categories", { name: name.trim() });
      toast.success("Category created");
      setName("");
      onRefresh();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      await api.delete(`/admin/categories/${id}`);
      toast.success("Category deleted");
      onRefresh();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center" data-testid="category-manager">
      <div className="absolute inset-0 bg-foreground/40" onClick={onClose} />
      <div className="relative bg-background w-full max-w-md rounded-xl shadow-2xl p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-display text-lg text-primary">Manage Categories</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="flex gap-2 mb-4">
          <input value={name} onChange={e => setName(e.target.value)} placeholder="New category name..." className="flex-1 bg-muted px-3 py-2.5 text-sm outline-none" data-testid="cat-name-input" onKeyDown={e => e.key === "Enter" && add()} />
          <button onClick={add} disabled={saving} className="bg-primary text-primary-foreground px-4 py-2 text-sm disabled:opacity-50" data-testid="cat-add-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          </button>
        </div>
        <div className="space-y-1 max-h-[300px] overflow-y-auto">
          {categories.map(c => (
            <div key={c.id} className="flex items-center justify-between px-3 py-2 bg-muted/50 text-sm group">
              <span>{c.name} <span className="text-muted-foreground text-xs">({c.slug})</span></span>
              <button onClick={() => remove(c.id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============ Shared Field Component ============ */
function EditorField({ label, value, onChange, type = "text", area, placeholder }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">{label}</span>
      {area ? (
        <textarea value={value || ""} onChange={onChange} rows={3} placeholder={placeholder} className="mt-1 w-full bg-muted px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary resize-none" />
      ) : (
        <input type={type} value={value ?? ""} onChange={onChange} placeholder={placeholder} className="mt-1 w-full bg-muted px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary" />
      )}
    </label>
  );
}

/* ============ Product Editor (Full Form) ============ */
function ProductEditor({ product, isNew, categories, onClose, onSaved }) {
  const [form, setForm] = useState(() => {
    const base = product ? { ...EMPTY_PRODUCT, ...product } : { ...EMPTY_PRODUCT };
    // Convert arrays to comma-separated strings for form inputs
    if (Array.isArray(base.tags)) base.tags = base.tags.join(", ");
    if (Array.isArray(base.variants)) base.variants = base.variants.join(", ");
    return base;
  });
  const [images, setImages] = useState(product?.images || []);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState("basic");
  const fileRef = useRef();

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [k]: v }));
  };

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/products/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        // Store the raw relative path — resolveImg is applied at render time
        setImages(prev => [...prev, data.url]);
      }
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async (targetStatus) => {
    if (!form.name.trim()) return toast.error("Product name is required");
    if (!form.category) return toast.error("Select a category");
    if (!Number(form.price) || Number(form.price) <= 0) return toast.error("Valid price is required");
    if (images.length === 0 && targetStatus === "published") return toast.error("Upload at least one image before publishing");

    setSaving(true);

    // Sanitize URL slug on frontend (strip URLs, special chars)
    let cleanSlug = (form.url_slug || "").trim();
    if (cleanSlug) {
      cleanSlug = cleanSlug.replace(/https?:\/\/[^/]*\/?/g, "").replace(/^\/[^/]+\//, "").replace(/^\/+|\/+$/g, "");
      cleanSlug = cleanSlug.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").replace(/-{2,}/g, "-");
    }

    // Convert comma-separated strings to arrays
    const tagsArr = typeof form.tags === "string" ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : (Array.isArray(form.tags) ? form.tags : []);
    const variantsArr = typeof form.variants === "string" ? form.variants.split(",").map(v => v.trim()).filter(Boolean) : (Array.isArray(form.variants) ? form.variants : []);

    const payload = {
      ...form,
      url_slug: cleanSlug,
      price: Number(form.price),
      mrp: Number(form.mrp) || Number(form.price),
      cost_price: form.cost_price ? Number(form.cost_price) : null,
      tax_rate: form.tax_rate ? Number(form.tax_rate) : null,
      stock_quantity: form.stock_quantity !== "" && form.stock_quantity != null ? Number(form.stock_quantity) : null,
      low_stock_threshold: Number(form.low_stock_threshold) || 5,
      num_pieces: form.num_pieces ? Number(form.num_pieces) : null,
      images,
      status: targetStatus || form.status,
      tags: tagsArr,
      variants: variantsArr,
    };

    // Set category_slug from selected category
    const cat = categories.find(c => c.name === form.category);
    if (cat) payload.category_slug = cat.slug;

    try {
      if (isNew) {
        await api.post("/admin/products", payload);
        toast.success(targetStatus === "published" ? "Product published!" : "Product saved as draft");
      } else {
        await api.put(`/admin/products/${product.id}`, payload);
        toast.success("Product updated");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const tabs = [
    { id: "basic", label: "Basic" },
    { id: "specs", label: "Specs" },
    { id: "pricing", label: "Pricing" },
    { id: "seo", label: "SEO" },
  ];

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="product-editor-full">
      <div className="absolute inset-0 bg-foreground/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-background h-full overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-secondary/80 backdrop-blur px-6 py-4 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl text-primary">{isNew ? "Add New Product" : "Edit Product"}</h2>
            <button onClick={onClose} data-testid="editor-close"><X size={22} /></button>
          </div>
          <div className="flex gap-1 mt-3">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} className={`px-3 py-1.5 text-xs rounded-lg ${tab === t.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* === Images === */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground mb-2">Images ({images.length})</p>
            <div className="flex flex-wrap gap-2">
              {images.map((img, idx) => (
                <div key={idx} className="relative w-20 h-24 bg-muted overflow-hidden group border border-border">
                  <img src={resolveImg(img)} alt="" className="w-full h-full object-cover" />
                  {idx === 0 && <span className="absolute top-0 left-0 bg-primary text-primary-foreground text-[8px] px-1.5">Primary</span>}
                  <div className="absolute inset-0 bg-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                    {idx > 0 && (
                      <button onClick={() => { const n = [...images]; [n[0], n[idx]] = [n[idx], n[0]]; setImages(n); }} className="bg-white p-1 rounded" title="Set as primary">
                        <Eye size={11} />
                      </button>
                    )}
                    <button onClick={() => setImages(images.filter((_, i) => i !== idx))} className="bg-white p-1 rounded text-destructive" title="Remove">
                      <Trash2 size={11} />
                    </button>
                  </div>
                </div>
              ))}
              <button onClick={() => fileRef.current?.click()} disabled={uploading} className="w-20 h-24 border border-dashed border-border flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-primary">
                {uploading ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
                <span className="text-[9px] mt-1">Upload</span>
              </button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={onFile} className="hidden" />
          </div>

          {/* === Basic Tab === */}
          {tab === "basic" && (
            <div className="space-y-4">
              <EditorField label="Product Name *" value={form.name} onChange={set("name")} placeholder="e.g. Mango Wood Serving Platter" />
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">Category *</span>
                  <select value={form.category} onChange={set("category")} className="mt-1 w-full bg-muted px-3 py-2 text-sm outline-none" data-testid="prod-category-select">
                    <option value="">Select category</option>
                    {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </label>
                <EditorField label="SKU" value={form.sku} onChange={set("sku")} placeholder="Auto-generated if blank" />
              </div>
              <EditorField label="Short Description" value={form.short_description} onChange={set("short_description")} placeholder="One-line summary" />
              <EditorField label="Full Description" value={form.description} onChange={set("description")} area placeholder="Detailed product description..." />
              <div className="grid grid-cols-3 gap-3">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.featured} onChange={set("featured")} /> Featured</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.new_arrival} onChange={set("new_arrival")} /> New Arrival</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.best_seller} onChange={set("best_seller")} /> Best Seller</label>
              </div>
              <EditorField label="Tags" value={form.tags} onChange={set("tags")} placeholder="comma separated: wooden, serving, platter" />
              <EditorField label="Variants" value={form.variants} onChange={set("variants")} placeholder="comma separated sizes/colours" />
            </div>
          )}

          {/* === Specs Tab === */}
          {tab === "specs" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <EditorField label="Material" value={form.material} onChange={set("material")} placeholder="e.g. Solid Mango Wood" />
                <EditorField label="Colour" value={form.colour} onChange={set("colour")} placeholder="e.g. Natural Brown" />
                <EditorField label="Dimensions" value={form.dimensions} onChange={set("dimensions")} placeholder="e.g. 40x28 cm" />
                <EditorField label="Weight" value={form.weight} onChange={set("weight")} placeholder="e.g. 1.2 kg" />
                <EditorField label="Shape" value={form.shape} onChange={set("shape")} placeholder="e.g. Oval, Rectangle" />
                <EditorField label="Finish" value={form.finish} onChange={set("finish")} placeholder="e.g. Hand-polished, Matte" />
                <EditorField label="Product Type" value={form.product_type} onChange={set("product_type")} placeholder="e.g. Serving Platter" />
                <EditorField label="Country of Origin" value={form.country_of_origin} onChange={set("country_of_origin")} placeholder="India" />
                <EditorField label="Set Contents" value={form.set_contents} onChange={set("set_contents")} placeholder="e.g. 1 platter + 2 bowls" />
                <EditorField label="Number of Pieces" value={form.num_pieces} onChange={set("num_pieces")} type="number" />
              </div>
              <EditorField label="Care Instructions" value={form.care} onChange={set("care")} area placeholder="How to clean and maintain..." />
            </div>
          )}

          {/* === Pricing Tab === */}
          {tab === "pricing" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <EditorField label="Selling Price (INR) *" value={form.price} onChange={set("price")} type="number" placeholder="999" />
                <EditorField label="MRP (INR)" value={form.mrp} onChange={set("mrp")} type="number" placeholder="1499" />
                <EditorField label="Cost Price (INR) — Admin only" value={form.cost_price} onChange={set("cost_price")} type="number" placeholder="Optional" />
                <EditorField label="Tax/GST (%)" value={form.tax_rate} onChange={set("tax_rate")} type="number" placeholder="18" />
              </div>
              <hr className="border-border" />
              <p className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">Inventory</p>
              <div className="grid grid-cols-2 gap-3">
                <EditorField label="Stock Quantity" value={form.stock_quantity} onChange={set("stock_quantity")} type="number" placeholder="Unlimited if blank" />
                <EditorField label="Low Stock Alert At" value={form.low_stock_threshold} onChange={set("low_stock_threshold")} type="number" />
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.in_stock} onChange={set("in_stock")} /> In Stock</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.track_inventory} onChange={set("track_inventory")} /> Track Inventory</label>
              </div>
            </div>
          )}

          {/* === SEO Tab === */}
          {tab === "seo" && (
            <div className="space-y-4">
              <EditorField label="SEO Title" value={form.seo_title} onChange={set("seo_title")} placeholder="e.g. Elegant Wooden Serving Platters | Aethel Decor" />
              <EditorField label="Meta Description" value={form.meta_description} onChange={set("meta_description")} area placeholder="Search engine description (150-160 chars)" />
              <EditorField label="URL Slug" value={form.url_slug} onChange={set("url_slug")} placeholder="e.g. wooden-serving-platters (auto-generated if blank)" />
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Preview:</p>
                <p className="text-sm text-blue-600 font-medium">{form.seo_title || form.name || "Product Title"}</p>
                <p className="text-xs text-green-700">aetheldecor.com/product/{form.url_slug || "..."}</p>
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{form.meta_description || form.short_description || form.description?.slice(0, 160) || "Product description..."}</p>
              </div>
            </div>
          )}

          {/* === Action Buttons === */}
          <div className="border-t border-border pt-4 space-y-2">
            {/* Preview button — works for saved products */}
            {!isNew && product?.id && (
              <button
                onClick={async () => {
                  try {
                    const { data } = await api.get(`/admin/products/${product.id}/preview-token`);
                    window.open(`/product/${product.id}?preview=${data.token}`, "_blank");
                  } catch { toast.error("Could not generate preview"); }
                }}
                className="w-full border border-border py-2.5 text-sm flex items-center justify-center gap-2 hover:bg-secondary"
                data-testid="prod-preview"
              >
                <ExternalLink size={14} /> Preview as Customer
              </button>
            )}
            <div className="flex flex-wrap gap-2">
              <button onClick={() => save("draft")} disabled={saving} className="flex-1 min-w-[120px] border border-border py-3 text-sm flex items-center justify-center gap-2 hover:bg-secondary disabled:opacity-50" data-testid="prod-save-draft">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Draft
              </button>
              <button onClick={() => save("published")} disabled={saving} className="flex-1 min-w-[120px] bg-primary text-primary-foreground py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-50" data-testid="prod-publish">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Publish size={14} />} Publish
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============ CSV Import Modal ============ */
function CsvImportModal({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  const doImport = async () => {
    if (!file) return;
    setImporting(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/products/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      if (data.created > 0) {
        toast.success(`${data.created} product(s) imported as drafts`);
        onImported();
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/admin/products/csv-template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "aethel_product_template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center" data-testid="csv-import-modal">
      <div className="absolute inset-0 bg-foreground/40" onClick={onClose} />
      <div className="relative bg-background w-full max-w-md rounded-xl shadow-2xl p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-display text-lg text-primary">Bulk Import Products</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>

        <p className="text-xs text-muted-foreground mb-4">Upload a CSV file to import multiple products at once. All imported products will be created as <strong>drafts</strong>.</p>

        <button onClick={downloadTemplate} className="text-xs text-primary hover:underline flex items-center gap-1 mb-4" data-testid="csv-download-template">
          <Download size={12} /> Download CSV Template
        </button>

        <div
          onClick={() => fileRef.current?.click()}
          className="border-2 border-dashed border-border hover:border-primary/40 bg-secondary/20 p-6 text-center cursor-pointer mb-4 transition-colors"
        >
          {file ? (
            <p className="text-sm text-foreground">{file.name} <span className="text-muted-foreground">({(file.size / 1024).toFixed(0)} KB)</span></p>
          ) : (
            <>
              <Upload size={24} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">Click to select CSV file</p>
            </>
          )}
        </div>
        <input ref={fileRef} type="file" accept=".csv" onChange={e => setFile(e.target.files[0])} className="hidden" data-testid="csv-file-input" />

        <button
          onClick={doImport}
          disabled={!file || importing}
          className="w-full bg-primary text-primary-foreground py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="csv-import-btn"
        >
          {importing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {importing ? "Importing..." : "Import Products"}
        </button>

        {result && (
          <div className="mt-4 p-3 bg-muted/50 rounded text-sm space-y-1" data-testid="csv-import-result">
            <p className="text-green-600 font-medium">{result.created} product(s) created as drafts</p>
            {result.errors > 0 && <p className="text-amber-600">{result.errors} row(s) skipped</p>}
            {result.error_details?.length > 0 && (
              <div className="text-xs text-muted-foreground mt-2 space-y-0.5">
                {result.error_details.map((e, i) => <p key={i}>{e}</p>)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============ Main Products Tab ============ */
export default function AdminProductsTab() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterStock, setFilterStock] = useState("all");
  const [sortBy, setSortBy] = useState("newest");

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort_by: sortBy };
      if (search) params.search = search;
      if (filterCat !== "all") params.category = filterCat;
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterStock !== "all") params.stock = filterStock;
      const { data } = await api.get("/admin/products", { params });
      setProducts(data);
    } catch {} finally { setLoading(false); }
  }, [search, filterCat, filterStatus, filterStock, sortBy]);

  const loadCategories = async () => {
    try { const { data } = await api.get("/admin/categories"); setCategories(data); } catch {}
  };

  const loadLowStock = async () => {
    try { const { data } = await api.get("/admin/products/low-stock"); setLowStockCount(data.count); } catch {}
  };

  useEffect(() => { loadProducts(); loadCategories(); loadLowStock(); }, [loadProducts]);

  const deleteProduct = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      const { data } = await api.delete(`/admin/products/${id}`);
      toast.success(data.action === "archived" ? "Product archived (has order history)" : "Product deleted");
      loadProducts();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const duplicateProduct = async (id) => {
    try {
      const { data } = await api.post(`/admin/products/${id}/duplicate`);
      toast.success("Product duplicated as draft");
      setEditing(data);
      loadProducts();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const toggleStatus = async (p) => {
    const newStatus = p.status === "published" ? "unpublished" : "published";
    try {
      await api.put(`/admin/products/${p.id}`, { status: newStatus });
      toast.success(`Product ${newStatus}`);
      loadProducts();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-products-tab">
      {/* Low Stock Alert */}
      {lowStockCount > 0 && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="low-stock-alert">
          <AlertTriangle size={16} className="flex-shrink-0" />
          <span><strong>{lowStockCount}</strong> product{lowStockCount > 1 ? "s" : ""} {lowStockCount > 1 ? "are" : "is"} at or below low stock threshold</span>
          <button onClick={() => setFilterStock("low")} className="ml-auto text-xs underline hover:text-amber-900">View</button>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => setCreating(true)} className="bg-primary text-primary-foreground px-5 py-2.5 text-sm flex items-center gap-2" data-testid="add-new-product">
          <Plus size={14} /> Add New Product
        </button>
        <button onClick={() => setShowImport(true)} className="border border-border px-4 py-2.5 text-sm flex items-center gap-2 hover:border-primary/40" data-testid="import-csv-btn">
          <Upload size={14} /> Import CSV
        </button>
        <button onClick={() => setShowCategories(true)} className="border border-border px-4 py-2.5 text-sm flex items-center gap-2 hover:border-primary/40" data-testid="manage-categories">
          <FolderPlus size={14} /> Categories
        </button>
        <div className="flex-1 min-w-[200px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or SKU..."
            className="w-full bg-muted pl-9 pr-3 py-2.5 text-sm outline-none"
            data-testid="product-search"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <Filter size={13} className="text-muted-foreground" />
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)} className="bg-muted px-3 py-1.5 text-xs outline-none" data-testid="filter-category">
          <option value="all">All Categories</option>
          {categories.map(c => <option key={c.id} value={c.slug}>{c.name}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="bg-muted px-3 py-1.5 text-xs outline-none" data-testid="filter-status">
          <option value="all">All Status</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
          <option value="unpublished">Unpublished</option>
        </select>
        <select value={filterStock} onChange={e => setFilterStock(e.target.value)} className="bg-muted px-3 py-1.5 text-xs outline-none">
          <option value="all">All Stock</option>
          <option value="in">In Stock</option>
          <option value="out">Out of Stock</option>
          <option value="low">Low Stock</option>
        </select>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="bg-muted px-3 py-1.5 text-xs outline-none">
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
          <option value="name_asc">Name A-Z</option>
          <option value="price_asc">Price Low-High</option>
          <option value="price_desc">Price High-Low</option>
        </select>
        <span className="text-xs text-muted-foreground ml-auto">{products.length} product(s)</span>
      </div>

      {/* Product Table */}
      {loading ? (
        <div className="py-12 text-center"><Loader2 size={24} className="animate-spin mx-auto text-primary" /></div>
      ) : products.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground">
          <Tag size={32} className="mx-auto mb-3 opacity-30" />
          <p>No products found. Click "Add New Product" to get started.</p>
        </div>
      ) : (
        <div className="border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/50">
                <th className="px-3 py-3 text-left text-[10px] uppercase tracking-wider text-muted-foreground w-14">Image</th>
                <th className="px-3 py-3 text-left text-[10px] uppercase tracking-wider text-muted-foreground">Product</th>
                <th className="px-3 py-3 text-left text-[10px] uppercase tracking-wider text-muted-foreground hidden sm:table-cell">SKU</th>
                <th className="px-3 py-3 text-left text-[10px] uppercase tracking-wider text-muted-foreground hidden md:table-cell">Category</th>
                <th className="px-3 py-3 text-right text-[10px] uppercase tracking-wider text-muted-foreground">Price</th>
                <th className="px-3 py-3 text-center text-[10px] uppercase tracking-wider text-muted-foreground hidden md:table-cell">Stock</th>
                <th className="px-3 py-3 text-center text-[10px] uppercase tracking-wider text-muted-foreground">Status</th>
                <th className="px-3 py-3 text-right text-[10px] uppercase tracking-wider text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p.id} className="border-b border-border/50 hover:bg-secondary/20" data-testid={`product-row-${p.id}`}>
                  <td className="px-3 py-2">
                    <div className="w-10 h-12 bg-muted overflow-hidden">
                      {p.images?.[0] ? <img src={resolveImg(p.images[0])} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><ImagePlus size={14} /></div>}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <p className="font-medium text-sm line-clamp-1">{p.name}</p>
                    <div className="flex gap-1 mt-0.5">
                      {p.featured && <span className="text-[9px] bg-accent/20 text-accent px-1.5 rounded">Featured</span>}
                      {p.new_arrival && <span className="text-[9px] bg-blue-50 text-blue-600 px-1.5 rounded">New</span>}
                      {p.best_seller && <span className="text-[9px] bg-green-50 text-green-600 px-1.5 rounded">Bestseller</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground font-mono hidden sm:table-cell">{p.sku || "-"}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground hidden md:table-cell">{p.category}</td>
                  <td className="px-3 py-2 text-right">
                    <p className="font-semibold text-primary text-xs">{inr(p.price)}</p>
                    {p.mrp > p.price && <p className="text-[10px] text-muted-foreground line-through">{inr(p.mrp)}</p>}
                  </td>
                  <td className="px-3 py-2 text-center hidden md:table-cell">
                    {!p.in_stock ? (
                      <span className="text-xs text-destructive">Out</span>
                    ) : p.stock_quantity != null && p.stock_quantity <= (p.low_stock_threshold || 5) ? (
                      <span className="text-xs text-amber-600 flex items-center justify-center gap-0.5" title="Low stock">
                        <AlertTriangle size={10} /> {p.stock_quantity}
                      </span>
                    ) : (
                      <span className="text-xs text-green-600">{p.stock_quantity != null ? p.stock_quantity : "Yes"}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button onClick={() => toggleStatus(p)} className={`text-[10px] px-2 py-0.5 rounded capitalize ${STATUS_BADGE[p.status] || STATUS_BADGE.draft}`}>
                      {p.status || "published"}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setEditing(p)} className="p-1.5 hover:bg-muted rounded" title="Edit" data-testid={`edit-${p.id}`}><Pencil size={13} /></button>
                      <button onClick={() => duplicateProduct(p.id)} className="p-1.5 hover:bg-muted rounded" title="Duplicate"><Copy size={13} /></button>
                      <button onClick={() => deleteProduct(p.id)} className="p-1.5 hover:bg-muted rounded text-muted-foreground hover:text-destructive" title="Delete"><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {creating && (
        <ProductEditor isNew categories={categories} onClose={() => setCreating(false)} onSaved={() => { loadProducts(); loadLowStock(); setCreating(false); }} />
      )}
      {editing && (
        <ProductEditor product={editing} isNew={false} categories={categories} onClose={() => setEditing(null)} onSaved={() => { loadProducts(); loadLowStock(); setEditing(null); }} />
      )}
      <CategoryManager open={showCategories} onClose={() => setShowCategories(false)} categories={categories} onRefresh={loadCategories} />
      <CsvImportModal open={showImport} onClose={() => setShowImport(false)} onImported={() => { loadProducts(); loadLowStock(); }} />
    </div>
  );
}
